<template>
  <v-dialog v-model="dialog" :max-width="options.width" persistent>
    <v-card>
      <v-card-title style="word-break: break-word;">{{ title }}</v-card-title>         
      <v-card-text v-show="!!message">
        <div v-for="(line, index) in message.split('\n')" :key="index">{{ line }}</div>
      </v-card-text>
      <div class="px-4">
        <IconSearch :prepopulated-search-term="this.selectedInventoryType.description" 
          @generateIcon="handleGenerateIcon" 
          @clearSearch="handleClearSearch" 
          @update:modelValue="emitSelection"
        />
      </div>
      <div class="pl-4">
        <v-icon 
          @click="togglePreview"
          icon="mdi-chevron-right"
          :class="{ 'rotate': showPreview }"
          
        />
        <span style="font-size: 0.9rem;">
          <span v-if="!showPreview">Show</span>
          <span v-else>Hide</span>
            icon preview</span>
      </div>
      <v-fade-transition>
        <v-card-text class="pa-4 preview-container" v-if="showPreview">
          <template v-if="isValidIcon(selectedIcon) && this.tempIcon === null">
            <v-icon :icon="selectedIcon" size="32" class="mb-2"/>
            <div class="mt-2">
              Do you want to set the above selected icon for the
              "{{ selectedInventoryType?.description || 'this' }}"
              inventory type?
            </div>
          </template>

          <template v-else-if="this.tempIcon !== null">
            <v-img :src="tempIcon.tempSavedIconUrl" max-width="32px" class="mb-2"/>
            <div class="mt-2">
              Do you want to set the above AI suggested icon for the
              "{{ selectedInventoryType?.description || 'this' }}"
              inventory type?
            </div>
          </template>
        </v-card-text>
      </v-fade-transition>
      <v-card-actions class="pt-0">
        <v-spacer/>
        <v-btn tile @mousedown.prevent="handleGenerate" :disabled="!searchTerm || isValidIcon(selectedIcon)"><v-icon small class="py-2 mr-2">mdi-robot</v-icon> AI Generate Icon</v-btn>
        <v-btn tile @mousedown.prevent="handleCancel">Cancel</v-btn>
        <v-btn tile @mousedown.prevent="handleConfirm" :disabled="!isValidIcon(selectedIcon) && this.tempIcon === null">OK</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import HTTP from '../plugins/backendAPI';
import { mapGetters } from 'vuex';
import IconSearch from './IconSearch.vue';
import { iconList } from '../helpers/iconList';

export default {
  components: {
    IconSearch
  },
  data: () => ({
    isConfirmed: false,
    dialog: false,
    resolve: null,
    reject: null,
    message: null,
    title: null,
    options: {
      color: 'primary',
      width: 290,
      zIndex: 3000
    },
    searchTerm: '',
    selectedIcon: null,
    tempIcon: null,
    showPreview: false
  }),
  props: {
    selectedInventoryType: Object
  },
  watch: {
    dialog(newValue) {
      if (!newValue && !this.isConfirmed) {
        this.resolve(false);
      }
    },
    searchTerm(newValue) {
      if (newValue === '') {
        this.selectedIcon = null;
        this.handleClearSearch();
      }
    },
    selectedIcon(newValue) {
      if (this.isValidIcon(newValue) && this.tempIcon === null) {
        this.showPreview = true;
      }
    },
    tempIcon(newValue) {
      if (newValue !== null) {
        this.showPreview = true;
      }
    }
  },
  computed: {
    ...mapGetters(["organizationid"]),
  },
  methods: {
    open(title, message, options) { 
      this.dialog = true;
      this.title = title;
      this.message = message;
      this.options = Object.assign(this.options, options);
      this.selectedIcon = null;
      this.searchTerm = this.selectedInventoryType?.description || '';
      this.selectedIcon = this.selectedInventoryType?.description || '';
      console.log(this.selectedInventoryType)
      this.getInventoryTypeIcon();

      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      })
    },
    isValidIcon(name) {
      return iconList.some(icon => icon.name === name);
    },
    async handleGenerate() {
      this.$store.dispatch("setLoading", true);
      await HTTP.get(`/inventorytype/${this.selectedInventoryType.id}/generate/icon`, { params: { organizationId: this.organizationid, searchTerm: this.searchTerm } })
      .then((response) => {
        this.tempIcon = response.data;
        if (this.isValidIcon(this.selectedIcon)) {
          this.selectedIcon = null; // Clear the selected icon to prevent confusion with the generated icon
        }
      })
      .catch((e) => {
          if (e.response.status !== 500) {
              this.$store.dispatch("showError", "Generating inventory type icon failed: " + (e.response.data.message ?? e.response.data));
              return;
          }
          this.$store.dispatch("showError", "Generating inventory type icon failed: Unknown server error.");
      })
      .finally(() => {
          this.$store.dispatch("setLoading", false);
      });
    },
    async handleConfirm() {
      const formData = new FormData();

      if (this.tempIcon !== null) {
        formData.append('iconTempDtoJson', JSON.stringify(this.tempIcon));
      } else if (this.selectedIcon !== null && this.isValidIcon(this.selectedIcon)) {
        formData.append('iconTempDtoJson', JSON.stringify({ name: this.selectedIcon, filename: this.selectedIcon, isSelectedFromIconLibrary: true }));
      }
                        
      formData.append('organizationId', this.organizationid);

      await HTTP.post(`/inventorytype/${this.selectedInventoryType.id}/save/icon`, formData, { headers: {'Content-Type': 'multipart/form-data'} })
      .then(async () => {
          this.tempIcon = null;
          this.selectedIcon = null;
          this.searchTerm = '';

          this.resolve('confirm');
      })
      .catch((e) => {
          if (e.response.status !== 500) {
              this.$store.dispatch("showError", "Saving inventory type icon failed: " + (e.response.data.message ?? e.response.data));
              return;
          }
          this.$store.dispatch("showError", "Saving inventory type icon failed: Unknown server error.");
      })
      .finally(() => {
          this.dialog = false;
          this.$store.dispatch("setLoading", false);
      });

    },
    async handleCancel() {
      this.tempIcon = null;
      this.selectedIcon = null;
      this.searchTerm = '';
      await HTTP.post(`/inventorytype/delete/tempicon`);

      this.showPreview = false;
      this.dialog = false;
      this.resolve('cancel');
    },
    async handleGenerateIcon(searchTerm) {
      this.searchTerm = searchTerm;
      await this.handleGenerate();
    },
    async handleClearSearch() {
       this.searchTerm = '';
       this.selectedIcon = null;
       if (this.tempIcon !== null) {
         this.tempIcon = null;
         await HTTP.post(`/inventorytype/delete/tempicon`);
       }
    },
    async emitSelection(selectedIcon) {
      this.selectedIcon = selectedIcon;
      this.searchTerm = selectedIcon || '';
       if (this.tempIcon !== null) {
         this.tempIcon = null; // Clear the generated icon if user selects a different icon from the library
         await HTTP.post(`/inventorytype/delete/tempicon`);
       }
    },
    getInventoryTypeIcon() {
      this.$store.dispatch("setLoading", true);
      HTTP.get(`/inventorytype/${this.selectedInventoryType.id}/get/icon`, { params: { organizationId: this.organizationid } }) //, newInventoryTypeDescription: this.searchTerm, workspaceId: this.workspaceid
      .then((response) => {
        this.tempIcon = response.data;
        if (this.isValidIcon(this.selectedIcon)) {
          this.selectedIcon = null; // Clear the selected icon to prevent confusion with the generated icon
        }
      })
      .catch((e) => {
          if (e.response.status !== 500) {
              this.$store.dispatch("showError", "Generating inventory type icon failed: " + (e.response.data.message ?? e.response.data));
              return;
          }
          this.$store.dispatch("showError", "Generating inventory type icon failed: Unknown server error.");
      })
      .finally(() => {
          this.$store.dispatch("setLoading", false);
      });
    },
    togglePreview() {
      this.showPreview = !this.showPreview;
    }
  }
}
</script>

<style scoped>
.preview-container {
  min-height: 120px;
}

.rotate {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transform-origin: center;
  transform: rotate(90deg);
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>